import { useMemo, type ReactNode } from "react";
import type { SmartStreamStore } from "../SmartStreamStore";
import { useLanggraphStream } from "../useLanggraphStream";
import { SmartStreamProvider } from "../SmartStreamProvider";
import { ComposerProvider } from "../composer/ComposerProvider";
import { ComposerManager } from "../composer/ComposerManager";
import { defaultFormatSubmit } from "../composer/formatContent";
import type { useLanggraphStreamOptions } from "../types";
import type { ComposerContent } from "../composer/types";
import { ChatRootProvider } from "./ChatContext";
import { useRef, useEffect } from "react";

// ─────────────────────────────────────────────────────────────────────────
// Props
// ─────────────────────────────────────────────────────────────────────────

interface ChatRootBaseProps {
  children: ReactNode;
  className?: string;
  /**
   * Hook that creates a ComposerManager bound to the store.
   * Created via `createComposer(config)`.
   * If omitted, a text-only default composer is created.
   */
  useComposer?: (store: SmartStreamStore<any, any>) => ComposerManager<any>;
  /**
   * Optional custom submit handler. Receives the default sendMessage as an argument
   * so callers can add side-effects (e.g. analytics) and still send the message.
   */
  onSubmit?: (sendMessage: () => void) => void;
  /**
   * When true, disables all input components (Textarea, SubmitButton, FileButton)
   * via context. Useful for locking the chat when the user is out of credits.
   */
  isInputDisabled?: boolean;
  /**
   * Called when the user clicks a disabled input component.
   * Useful for showing a modal (e.g. "purchase credits").
   */
  onDisabledClick?: () => void;
  /**
   * When true (default), Submit + Enter stay enabled while a stream is
   * in flight, and submissions during a run auto-route to the agent
   * inbox endpoint instead of creating a new run. The user perceives
   * a "type while the agent works" UX (Claude Code style).
   *
   * Set false to restore the legacy "block while streaming" behavior.
   */
  concurrentSubmit?: boolean;
  /** Names + avatars for the message lockups ("Houston" / "You"). */
  personas?: { assistant?: { name: string; avatar?: ReactNode }; user?: { name: string; avatar?: ReactNode } };
}

interface ChatRootWithStreamOptions extends ChatRootBaseProps {
  /**
   * Mode 1: Chat.Root owns the stream (recommended — acts as render firewall).
   */
  streamOptions: useLanggraphStreamOptions<any, any>;
  store?: never;
}

interface ChatRootWithStore extends ChatRootBaseProps {
  /**
   * Mode 2: Pre-built store (advanced — caller manages render firewall).
   */
  store: SmartStreamStore<any, any>;
  streamOptions?: never;
}

export type ChatRootProps = ChatRootWithStreamOptions | ChatRootWithStore;

// ─────────────────────────────────────────────────────────────────────────
// Default text-only composer
// ─────────────────────────────────────────────────────────────────────────

function useDefaultComposer(store: SmartStreamStore<any, any>): ComposerManager<any> {
  const storeRef = useRef(store);
  storeRef.current = store;

  const managerRef = useRef<ComposerManager<any> | null>(null);
  if (!managerRef.current) {
    managerRef.current = new ComposerManager<any>(
      (content: ComposerContent) => {
        const { submit } = storeRef.current.getSnapshot();
        submit(defaultFormatSubmit(content));
      },
    );
  }

  useEffect(() => {
    return () => {
      managerRef.current?.clear();
    };
  }, []);

  return managerRef.current;
}

// ─────────────────────────────────────────────────────────────────────────
// Inner component — wraps children with providers using an existing store
// ─────────────────────────────────────────────────────────────────────────

function ChatRootInner({
  store,
  children,
  className,
  useComposer: useComposerHook,
  onSubmit,
  isInputDisabled,
  onDisabledClick,
  concurrentSubmit,
  personas,
}: ChatRootBaseProps & { store: SmartStreamStore<any, any> }) {
  const defaultComposer = useDefaultComposer(store);
  const composer = useComposerHook ? useComposerHook(store) : defaultComposer;

  const rootCtx = useMemo(
    () => ({ onSubmit, isInputDisabled, onDisabledClick, concurrentSubmit, personas }),
    [onSubmit, isInputDisabled, onDisabledClick, concurrentSubmit, personas]
  );

  return (
    <SmartStreamProvider store={store}>
      <ComposerProvider composer={composer}>
        <ChatRootProvider value={rootCtx}>
          {className ? <div className={className}>{children}</div> : children}
        </ChatRootProvider>
      </ComposerProvider>
    </SmartStreamProvider>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Mode 1: Chat.Root owns the stream (render firewall)
// ─────────────────────────────────────────────────────────────────────────

function ChatRootWithOwnedStream({
  streamOptions,
  children,
  ...rest
}: ChatRootWithStreamOptions) {
  const store = useLanggraphStream(streamOptions);
  return (
    <ChatRootInner store={store} {...rest}>
      {children}
    </ChatRootInner>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Public component
// ─────────────────────────────────────────────────────────────────────────

/**
 * Chat.Root — the entry point for the Chat compound component system.
 *
 * Supports two modes:
 *
 * Mode 1 (recommended): Pass `streamOptions` and Chat.Root creates + owns the stream.
 * This makes Chat.Root the render firewall — children only re-render via selectors.
 *
 * ```tsx
 * <Chat.Root streamOptions={{ apiUrl, assistantId, threadId }}>
 *   <Chat.Messages.List>...</Chat.Messages.List>
 *   <Chat.Input.Textarea />
 * </Chat.Root>
 * ```
 *
 * Mode 2 (advanced): Pass a pre-built `store` from useLanggraphStream.
 * The caller is responsible for the render firewall.
 *
 * ```tsx
 * const store = useLanggraphStream({ ... });
 * <Chat.Root store={store}>
 *   ...
 * </Chat.Root>
 * ```
 */
export function ChatRoot(props: ChatRootProps) {
  if ("streamOptions" in props && props.streamOptions) {
    return <ChatRootWithOwnedStream {...props as ChatRootWithStreamOptions} />;
  }

  return <ChatRootInner {...props as ChatRootWithStore & ChatRootBaseProps} />;
}
